<template>
  <div class="card">
    <div class="bg-dark card-header text-white">Academic Indirect Cost Distribution - Documents</div>
    <div class="card-body">
      <AppLoadingContainer :predicate="isLoading">
        <BTable :fields="tableFields" :items="attachments" :responsive="true" primary-key="id" show-empty table-class="table-align-middle" :tbody-tr-class="selectRow">
          <template v-slot:cell(selected)="row">
            <b-form-radio v-model="activeRow" :value="row.item.attachmentType"></b-form-radio>
          </template>
          <template v-slot:cell(uploaded)="row">
            <span :style="row.item.uploaded ? 'color: green;' : 'color: red;'">{{ row.item.uploaded | boolOptions(false, "Uploaded", "Missing") }}</span>
          </template>
          <template v-slot:cell(attachmentType)="row">
            <span>{{ row.item.attachmentType | enumName(attachmentTypeEnum) }}</span>
          </template>
          <template v-slot:cell(actions)="row">
            <div class="btn-group btn-group-sm">
              <button v-if="canEdit" @click="showAddAttachmentModal(row.item.attachmentType)" class="btn btn-secondary btn d-print-none" :disabled="disableButtons || row.item.attachmentType !== activeRow">
                <i style="margin-right: 0px;" class="fas fa-file-upload"></i>
              </button>
              <button v-if="row.item.uploaded" @click="downloadAttachment(row.item.id)" class="btn btn-primary btn d-print-none" :disabled="disableButtons || row.item.attachmentType !== activeRow">
                <i style="margin-right: 0px;" class="fas fa-file-download"></i>
              </button>
            </div>
          </template>
          <template v-slot:cell(createdDate)="row">
            <span v-if="row.item.uploaded">{{ row.item.createdDate | moment('MM/DD/YYYY') }}</span>
          </template>
        </BTable>

        <b-modal header-class="bg-maroon text-white" id="add-attachment-modal" ref="addAttachmentModal" title="Add Document" @ok="onAddAttachment">     
          <AppAlertNote>
            Existing {{ newAttachmentType | enumName(attachmentTypeEnum) }} documents will be replaced with the new document uploaded
          </AppAlertNote>
          <b-form @submit.stop.prevent="onAddAttachment">
            <b-form-group label="Document">
              <b-form-file
                v-model="newAttachment"
                :state="Boolean(newAttachment)"
                placeholder="Choose a file"
                accept="application/pdf"
              ></b-form-file>
            </b-form-group>
          </b-form>
          <template v-slot:modal-footer="{ cancel }">
            <button :disabled="isSubmitting" class="btn btn-primary" type="submit" @click="onAddAttachment">Save</button>
            <button @click="cancel" class="btn btn-danger">Close</button>
          </template>
        </b-modal>
      </AppLoadingContainer>
    </div>
  </div>
</template>

<script>
import AppAlertNote from "@/components/shared/AppAlertNote";
import { boolOptions } from "@/filters/Boolean";
import { enumName } from "@/filters/Enum";
import AppLoadingContainer from "@/components/shared/AppLoadingContainer";
import AcademicIndirectCostService from "../../services/AcademicIndirectCostService";
import { ToastType } from "@/services/models/shared/ToastType";
import { AttachmentType } from "@/services/models/AcademicIndirectCost"
import EnumExtensions from '@/utilities/extensions/EnumExtensions';

export default {
  components: {
    AppAlertNote,
    AppLoadingContainer
  },
  filters: {
    boolOptions,
    enumName
  },
  props: {
    payPeriodId: {
      required: true,
      type: String
    },
    canEdit: {
      required: true,
      type: Boolean
    }
  },
  data() {
    return {
      attachments: [],
      deletingRow: null,
      isLoading: false,
      newAttachment: null,
      newAttachmentType: null,
      isSubmitting: false,
      disableButtons: false,
      downloadAttachmentId: null,
      activeRow: null,
      tableFields: [
        { key: 'selected', label: '' },
        { key: 'attachmentType', label: 'Document Type', tdClass: 'text-center', thClass: 'text-center' },
        { key: 'actions', label: '', thStyle: 'width:100px', tdClass: 'text-center' },
        { key: 'uploaded', label: 'Uploaded', thStyle: 'width:150px', tdClass: 'text-center', thClass: 'text-center' },
        { key: 'fileName', label: 'File Name' },
        { key: 'createdByPersonName', label: 'Added By' },
        { key: 'createdDate', label: 'Date' },
      ]
    };
  },
  computed: {
    attachmentTypeEnum() {
      return AttachmentType;
    },

    attachmentTypes() {
      return EnumExtensions.EnumToSelectList(AttachmentType);
    }
  },
  methods: {
    loadData() {
      this.isLoading = true;
      AcademicIndirectCostService.getAttachmentList(this.payPeriodId)
        .then((response) => {
          if (response && response.length > 0) {
            //Add missing types
            this.attachmentTypes.forEach(item => {
              const index = response.findIndex(obj => obj.attachmentType === item.value);
              if (index === -1) {
                this.attachments.push({
                  attachmentType: item.value, 
                  id: null, 
                  fileName: null, 
                  createdByPersonName: null, 
                  createdDate: null, 
                  uploaded: false
                });
              } else {
                this.attachments.push({
                  attachmentType: response[index].attachmentType, 
                  id: response[index].id, 
                  fileName: response[index].fileName, 
                  createdByPersonName: response[index].createdByPersonName, 
                  createdDate: response[index].createdDate, 
                  uploaded: true
                })
              }
            });
          } else {
            this.attachments = this.attachmentTypes.map(item => { return { attachmentType: item.value, id: null, fileName: null, createdByPersonName: null, createdDate: null, uploaded: false }; }); 
          }
        })
        .catch(() => {
          this.alert(ToastType.Danger, 'Failed to load attachments');
        })
        .finally(() => {
          this.isLoading = false;
          this.activeRow = this.attachments[0].attachmentType;
        });
    },
    showAddAttachmentModal(type) {
      this.newAttachmentType = type;
      this.$refs.addAttachmentModal.show();
    },
    onAddAttachment() {
      this.isSubmitting = true;
      const formData = new FormData();
      if (this.newAttachment !== null) {
        formData.append('submittedByPersonId', this.$store.state.user.id);
        formData.append('file', this.newAttachment);
        formData.append('attachmentType', this.newAttachmentType);
        AcademicIndirectCostService.addAttachment(this.payPeriodId, formData)
          .then(() => {
            this.alert(ToastType.Success, 'The attachment was successfully uploaded');
            this.attachments = [];
            this.loadData(); // Reload the attachments
          })
          .catch(() => {
            this.alert(ToastType.Danger, 'Failed to upload attachment');
          })
          .finally(() => {
            this.isSubmitting = false;
            this.$refs.addAttachmentModal.close();
            this.clearModal();
          });
      } else {
        this.isSubmitting = false;
      }
    },
    clearModal() {
      this.newAttachment = null;
      this.newAttachmentType = null;
    },
    downloadAttachment(id) {
      this.disableButtons = true;
      this.downloadAttachmentId = id;
      AcademicIndirectCostService.downloadAttachment(id)
        .catch(() => {
          this.alert(ToastType.Danger, "Unable to download attachment")
        })
        .finally(() => {
          this.disableButtons = false,
          this.downloadAttachmentId = null;
        })
    },

    selectRow(item) {
      if (item.attachmentType === this.activeRow) return "selected";
      else return "notSelected";
    }
  },
  created() {
    this.loadData();
  },
};
</script>

<style>
tr.selected { 
  background-color: rgba(0,0,0,.05);
}

tr.notSelected {
  color: lightslategrey !important;
}
</style>